/**
 * Grid Canvas Component
 * Main canvas for rendering invoice templates with rulers and grid overlay
 */

import { useState, useMemo } from 'react';
import type { ContainerElement, AbsoluteElement } from '../../types/template.types';
import { ElementTypeSelector } from '../ui/element-type-selector';
import { RulerOverlay } from './ruler-overlay';
import { AbsoluteElementsLayer } from '../absolute-positioning/absolute-elements-layer';
import { HeaderFooterSection } from './header-footer-section';
import { MainContentSection } from './main-content-section';
import { useCompanyDetails } from '@/features/company/hooks/use-company-details';
import { 
  createInsertHandler,
  createElementClickHandler,
  createDeleteElementHandler,
  createScrollHandler,
  createSelectTypeHandler,
  type PendingInsert
} from '../../utils/canvas-event-handlers';

// Constants
const A4_WIDTH = 794;
const A4_HEIGHT = 1123;

// Calculate minimum height needed for a container based on its content
const calculateContainerMinHeight = (container: ContainerElement): number => {
  const rows = container.rows || 0;
  const columns = container.columns || 0;
  
  if (rows < 1 || columns < 1) return 0;
  
  const rowHeights: number[] = [];
  
  for (let row = 0; row < rows; row++) {
    let maxHeightInRow = 0;
    
    for (let col = 0; col < columns; col++) {
      const child = container.children?.find(
        (c) => c.cellRow === row && c.cellColumn === col
      );
      
      if (child) {
        const element = child.element;
        let contentHeight = 0;
        
        // Estimate height based on element type
        if (element.height) {
          contentHeight = element.height;
        } else if (element.type === 'container') {
          contentHeight = calculateContainerMinHeight(element as ContainerElement);
        } else if (element.type === 'text' || element.type === 'message') {
          // Estimate text height: count lines × fontSize × lineHeight
          const fontSize = element.fontSize || 12;
          const lineHeight = element.lineHeight || 1.5;
          const content = element.content || '';
          const lines = content.split('\n').length;
          contentHeight = fontSize * lineHeight * lines;
        } else if (element.type === 'qrcode' || element.type === 'image') {
          contentHeight = element.height || 100;
        } else {
          contentHeight = 60; // Default height
        }
        
        // Add margins
        contentHeight += (element.marginTop || 0) + (element.marginBottom || 0);
        
        maxHeightInRow = Math.max(maxHeightInRow, contentHeight);
      }
    }
    
    rowHeights.push(maxHeightInRow || 20); // Minimum 20px per row
  }
  
  const totalContentHeight = rowHeights.reduce((sum, h) => sum + h, 0);
  const gapSize = container.gap ?? 8;
  const gapCount = Math.max(0, rows - 1);
  const totalGaps = gapCount * gapSize;
  
  return totalContentHeight + totalGaps;
};

// DIN C4 Envelope Window (DIN 5008)
// Conversion: mm × 96 / 25.4 = px at 96 DPI
const C4_WINDOW = {
  LEFT: 75.6,    // 20mm from left edge
  WIDTH: 340.2,  // 90mm wide
  HEIGHT: 170.1, // 45mm high
  FORM_A_TOP: 102.0, // 27mm from top edge (Form A - traditional)
  FORM_B_TOP: 170.1, // 45mm from top edge (Form B - modern)
};

type EnvelopeWindowType = 'none' | 'form-a' | 'form-b';

interface GridCanvasProps {
  rootContainer: ContainerElement | ContainerElement[] | null;
  onChange: (rootContainer: ContainerElement | ContainerElement[] | null) => void;
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
  
  // Absolutely positioned elements
  absoluteElements?: AbsoluteElement[];
  onAbsoluteElementsChange?: (elements: AbsoluteElement[]) => void;
  onCutAbsoluteElement?: (elementId: string) => void;
  onCopyAbsoluteElement?: (elementId: string) => void;
  onPasteAbsoluteElement?: () => void;
  onDuplicateAbsoluteElement?: (elementId: string) => void;
  onBringToFront?: (elementId: string) => void;
  onSendToBack?: (elementId: string) => void;
  onBringForward?: (elementId: string) => void;
  onSendBackward?: (elementId: string) => void;
  hasClipboard?: boolean;
  
  // Header and Footer containers
  headerContainer?: ContainerElement;
  onHeaderChange?: (container: ContainerElement) => void;
  footerContainer?: ContainerElement;
  onFooterChange?: (container: ContainerElement) => void;
  
  // Page settings
  fontFamily: string;
  fontSize: number;
  fontColor: string;
  showGrid?: boolean;
  gridSize?: number;
  showRulers?: boolean;
  leftMargin?: number;
  rightMargin?: number;
  topMargin?: number;
  bottomMargin?: number;
  showMargins?: boolean;
  showEnvelopeWindow?: EnvelopeWindowType;
  
  // Ref for screenshot capture (document only, no rulers)
  documentRef?: React.RefObject<HTMLDivElement>;
}



export function GridCanvas({
  rootContainer,
  onChange,
  selectedElementId,
  onSelectElement,
  absoluteElements = [],
  onAbsoluteElementsChange,
  onCutAbsoluteElement,
  onCopyAbsoluteElement,
  onPasteAbsoluteElement,
  onDuplicateAbsoluteElement,
  onBringToFront,
  onSendToBack,
  onBringForward,
  onSendBackward,
  hasClipboard = false,
  headerContainer,
  onHeaderChange,
  footerContainer,
  onFooterChange,
  fontFamily,
  fontSize,
  fontColor,
  showGrid = false,
  gridSize = 9.45,
  showRulers = true,
  leftMargin = 56.7,
  rightMargin = 56.7,
  topMargin = 56.7,
  bottomMargin = 56.7,
  showMargins = true,
  showEnvelopeWindow = 'none',
  documentRef,
}: GridCanvasProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingInsert, setPendingInsert] = useState<PendingInsert | null>(null);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });

  // Load company details for sender element
  const { data: companyDetails } = useCompanyDetails();

  const RULER_SIZE = 20;
  const RULER_OFFSET = 40; // Offset for rulers from top/left edge

  // Normalize rootContainer to always be an array and update nested container heights
  const containers: ContainerElement[] = useMemo(() => {
    const rawContainers = Array.isArray(rootContainer) 
      ? rootContainer 
      : rootContainer 
        ? [rootContainer] 
        : [];
    
    // Update nested container heights for all containers
    return rawContainers;
  }, [rootContainer]);

  // Calculate root container IDs (header, footer, main containers)
  const rootContainerIds = useMemo(() => {
    const ids: string[] = [];
    if (headerContainer) ids.push(headerContainer.id);
    if (footerContainer) ids.push(footerContainer.id);
    containers.forEach(c => ids.push(c.id));
    return ids;
  }, [headerContainer, footerContainer, containers]);

  // Event handlers created from utility functions
  const handleInsertClick = createInsertHandler(setPendingInsert, setDialogOpen);
  const handleElementClick = createElementClickHandler(containers, headerContainer, footerContainer, selectedElementId, onSelectElement);
  const handleDeleteElement = createDeleteElementHandler(containers, onChange, onSelectElement);
  const handleScroll = createScrollHandler(setScrollPosition);
  const handleSelectType = createSelectTypeHandler(
    containers,
    headerContainer,
    footerContainer,
    topMargin,
    bottomMargin,
    pendingInsert,
    onChange,
    onHeaderChange,
    onFooterChange,
    setPendingInsert,
    setDialogOpen
  );

  return (
    <div className="relative flex bg-muted">
      {/* Rulers */}
      {showRulers && (
        <RulerOverlay
          scrollPosition={scrollPosition}
          rulerSize={RULER_SIZE}
          rulerOffset={RULER_OFFSET}
          pageWidth={A4_WIDTH}
          pageHeight={A4_HEIGHT}
        />
      )}

      {/* Canvas */}
      <div
        className="flex-1 overflow-auto"
        style={{ 
          marginLeft: showRulers ? RULER_SIZE + RULER_OFFSET : 0, 
          marginTop: showRulers ? RULER_SIZE + RULER_OFFSET : 0 
        }}
        onClick={() => onSelectElement(null)}
        onScroll={handleScroll}
      >
        <div 
          ref={documentRef}
          className="relative shadow-lg inline-block" 
          style={{ 
            width: A4_WIDTH, 
            height: A4_HEIGHT, 
            overflow: 'hidden',
            backgroundColor: '#ffffff', // Always white for print preview
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onSelectElement(null);
            }
          }}
        >
          {/* Grid overlay */}
          {showGrid && (
            <svg
              className="absolute inset-0 pointer-events-none"
              width={A4_WIDTH}
              height={A4_HEIGHT}
              style={{ zIndex: 1 }}
            >
              <defs>
                <pattern
                  id="grid-pattern"
                  width={gridSize}
                  height={gridSize}
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="0.5"
                    className="text-muted-foreground/40"
                  />
                </pattern>
              </defs>
              <rect width={A4_WIDTH} height={A4_HEIGHT} fill="url(#grid-pattern)" />
            </svg>
          )}

          {/* Margin indicators */}
          {showMargins && (
            <div
              className="absolute border border-dashed border-primary pointer-events-none"
              style={{
                left: leftMargin,
                right: rightMargin,
                top: topMargin,
                bottom: bottomMargin,
                zIndex: 9999,
              }}
            />
          )}

          {/* DIN C4 Envelope Window Overlay */}
          {showEnvelopeWindow !== 'none' && (
            <div
              className="absolute border-2 border-dashed border-destructive pointer-events-none"
              style={{
                left: C4_WINDOW.LEFT,
                top: showEnvelopeWindow === 'form-a' ? C4_WINDOW.FORM_A_TOP : C4_WINDOW.FORM_B_TOP,
                width: C4_WINDOW.WIDTH,
                height: C4_WINDOW.HEIGHT,
                zIndex: 10000,
                backgroundColor: 'var(--destructive)',
                opacity: 0.1,
              }}
              title={showEnvelopeWindow === 'form-a' 
                ? 'DIN C4 Sichtfenster Form A (20mm × 27mm, 90mm × 45mm)'
                : 'DIN C4 Sichtfenster Form B (20mm × 45mm, 90mm × 45mm)'}
            />
          )}

          {/* Container for all elements (grid and absolute) with shared z-index space */}
          <div className="relative" style={{ height: A4_HEIGHT, width: A4_WIDTH, overflow: 'hidden' }}>
            {/* Absolutely positioned elements */}
            <AbsoluteElementsLayer
              elements={absoluteElements}
              selectedElementId={selectedElementId}
              onSelectElement={onSelectElement}
              onElementsChange={onAbsoluteElementsChange}
              onCutElement={onCutAbsoluteElement}
              onCopyElement={onCopyAbsoluteElement}
              onPasteElement={onPasteAbsoluteElement}
              onDuplicateElement={onDuplicateAbsoluteElement}
              onBringToFront={onBringToFront}
              onSendToBack={onSendToBack}
              onBringForward={onBringForward}
              onSendBackward={onSendBackward}
              hasClipboard={hasClipboard}
              gridSize={showGrid ? gridSize : undefined}
            />
            
            {/* Header Container */}
            {headerContainer && onHeaderChange && (
              <HeaderFooterSection
                container={headerContainer}
                type="header"
                height={topMargin}
                pageWidth={A4_WIDTH}
                pageHeight={A4_HEIGHT}
                containerPath={[-1]}
                selectedElementId={selectedElementId}
                onSelectElement={handleElementClick}
                onInsertClick={handleInsertClick}
                onContainerChange={onHeaderChange}
                companyDetails={companyDetails}
                fontFamily={fontFamily}
                fontSize={fontSize}
                fontColor={fontColor}
                topMargin={topMargin}
                bottomMargin={bottomMargin}
                leftMargin={leftMargin}
                rightMargin={rightMargin}
                rootContainerIds={rootContainerIds}
              />
            )}
            
            {/* Main Content Section */}
            <MainContentSection
              containers={containers}
              pageWidth={A4_WIDTH}
              pageHeight={A4_HEIGHT}
              leftMargin={leftMargin}
              rightMargin={rightMargin}
              topMargin={topMargin}
              bottomMargin={bottomMargin}
              selectedElementId={selectedElementId}
              onSelectElement={handleElementClick}
              onInsertClick={handleInsertClick}
              onDeleteElement={handleDeleteElement}
              companyDetails={companyDetails}
              fontFamily={fontFamily}
              fontSize={fontSize}
              fontColor={fontColor}
              rootContainerIds={rootContainerIds}
            />
            
            {/* Footer Container */}
            {footerContainer && onFooterChange && (
              <HeaderFooterSection
                container={footerContainer}
                type="footer"
                height={bottomMargin}
                pageWidth={A4_WIDTH}
                pageHeight={A4_HEIGHT}
                containerPath={[-2]}
                selectedElementId={selectedElementId}
                onSelectElement={handleElementClick}
                onInsertClick={handleInsertClick}
                onContainerChange={onFooterChange}
                companyDetails={companyDetails}
                fontFamily={fontFamily}
                fontSize={fontSize}
                fontColor={fontColor}
                topMargin={topMargin}
                bottomMargin={bottomMargin}
                leftMargin={leftMargin}
                rightMargin={rightMargin}
                rootContainerIds={rootContainerIds}
              />
            )}
          </div>
        </div>
      </div>

      {/* Element type selector dialog */}
      <ElementTypeSelector
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setPendingInsert(null);
        }}
        onSelectType={handleSelectType}
        onlyContainer={!pendingInsert || (pendingInsert.cellRow === undefined && pendingInsert.cellColumn === undefined)}
        isHeaderInsert={pendingInsert?.containerPath[0] === -1}
        isFooterInsert={pendingInsert?.containerPath[0] === -2}
      />
    </div>
  );
}
